import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { layoutLaneIntervals } from '../productionTrackLayout';
import {
  barClassForInterval,
  buildProductionTrackSections,
  formatIntervalTooltip,
} from '../productionTrackRows';
import { TRACK_ZOOM_STEPS, trackZoomFactor } from '../trackZoom';
import './ProductionTrack.css';

/** Row height sized to bar font (10px) + padding — not oversized empty lanes. */
const LANE_HEIGHT = 16;
const LABEL_COL_PX = 100;
const MIN_BAR_PX = 4;
const LABEL_MIN_PX = 28;

function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function buildAxisTicks(maxT, trackWidthPx) {
  if (maxT <= 0 || trackWidthPx <= 0) return [0, maxT];
  const targetPx = 80;
  const roughStep = (maxT * targetPx) / trackWidthPx;
  const niceSteps = [5, 10, 15, 30, 60, 120, 180, 300, 600];
  const step = niceSteps.find((s) => s >= roughStep) || Math.ceil(roughStep / 60) * 60;
  const ticks = [];
  for (let t = 0; t <= maxT + 0.001; t += step) {
    ticks.push(Math.min(maxT, t));
  }
  if (ticks[ticks.length - 1] < maxT - 0.5) ticks.push(maxT);
  return ticks;
}

const ProductionRowBars = memo(function ProductionRowBars({
  laid,
  rowKind,
  maxT,
  timelineWidthPx,
  onSeekRef,
  onHoverRef,
}) {
  return (laid || []).map((interval, idx) => {
    const start = interval.start_real_time || 0;
    const finish = interval.finish_real_time || start;
    const leftPx = (start / maxT) * timelineWidthPx;
    const widthPx = Math.max(MIN_BAR_PX, ((finish - start) / maxT) * timelineWidthPx);
    const barClass = barClassForInterval(interval, rowKind);
    const tip = formatIntervalTooltip(interval);
    const showText = widthPx >= LABEL_MIN_PX;
    return (
      <button
        key={`${rowKind}-${idx}-${start}-${interval.name}`}
        type="button"
        className={`production-bar bar-${barClass}`}
        style={{
          left: `${leftPx}px`,
          width: `${widthPx}px`,
          top: `${interval.top}px`,
          height: `${Math.max(2, interval.height - 1)}px`,
        }}
        aria-label={tip}
        onMouseEnter={(e) => {
          onHoverRef.current?.({
            kind: 'production',
            text: tip,
            interval,
            clientX: e.clientX,
            clientY: e.clientY,
          });
        }}
        onMouseMove={(e) => {
          onHoverRef.current?.({
            kind: 'production',
            text: tip,
            interval,
            clientX: e.clientX,
            clientY: e.clientY,
          });
        }}
        onMouseLeave={() => onHoverRef.current?.(null)}
        onClick={(e) => {
          e.stopPropagation();
          onSeekRef.current?.(start);
        }}
      >
        {showText ? <span>{interval.name}</span> : <span className="production-bar-dot" />}
      </button>
    );
  });
});

/**
 * Production Track — collapsible producer/economy rows + Playhead.
 * Zoom index is owned by ReplayPlayer so Losses Track shares the same scale.
 */
const ProductionTrack = ({
  intervals,
  duration,
  time,
  playing = false,
  onSeek,
  playerName,
  zoomIndex = 0,
  onZoomIndexChange,
  onHoverChange,
  scrollLeft = 0,
  onScrollLeftChange,
}) => {
  const scrollRef = useRef(null);
  const onSeekRef = useRef(onSeek);
  onSeekRef.current = onSeek;
  const onHoverRef = useRef(onHoverChange);
  onHoverRef.current = onHoverChange;
  const [viewportWidth, setViewportWidth] = useState(0);

  const maxT = Math.max(
    duration || 0,
    ...(intervals || []).map((i) => i.finish_real_time || 0),
    1
  );

  const sections = useMemo(
    () => buildProductionTrackSections(intervals),
    [intervals]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setViewportWidth(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [intervals, sections]);

  // Viewport includes label column; timeline is the remainder.
  const timelineViewport = Math.max(0, viewportWidth - LABEL_COL_PX - 8);
  const fitPxPerSecond = timelineViewport > 0 ? timelineViewport / maxT : 1;
  const zoom = trackZoomFactor(zoomIndex);
  const timelineWidthPx = Math.max(timelineViewport, maxT * fitPxPerSecond * zoom);
  const innerWidthPx = LABEL_COL_PX + 8 + timelineWidthPx;

  const axisTicks = useMemo(
    () => buildAxisTicks(maxT, timelineWidthPx),
    [maxT, timelineWidthPx]
  );

  const laidSections = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => ({
          ...row,
          laid: layoutLaneIntervals(row.intervals, LANE_HEIGHT),
        })),
      })),
    [sections]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || scrollLeft == null) return;
    if (Math.abs(el.scrollLeft - scrollLeft) > 1) {
      el.scrollLeft = scrollLeft;
    }
  }, [scrollLeft, timelineWidthPx]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !playing || zoom <= 1) return;
    const x = LABEL_COL_PX + 8 + (time / maxT) * timelineWidthPx;
    const margin = el.clientWidth * 0.25;
    if (x < el.scrollLeft + margin) {
      const next = Math.max(0, x - margin);
      el.scrollLeft = next;
      onScrollLeftChange?.(next);
    } else if (x > el.scrollLeft + el.clientWidth - margin) {
      const next = Math.min(
        el.scrollWidth - el.clientWidth,
        x - el.clientWidth + margin
      );
      el.scrollLeft = next;
      onScrollLeftChange?.(next);
    }
  }, [time, playing, zoom, maxT, timelineWidthPx, onScrollLeftChange]);

  if (!intervals || intervals.length === 0) {
    return (
      <div className="production-track empty" data-testid="production-track">
        <h4>Production Track{playerName ? ` · ${playerName}` : ''}</h4>
        <p>No Production Intervals for this player.</p>
      </div>
    );
  }

  const handleTrackClick = (event) => {
    if (!onSeekRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = event.clientX - rect.left + el.scrollLeft - LABEL_COL_PX - 8;
    if (x < 0) return;
    const ratio = Math.min(1, Math.max(0, x / timelineWidthPx));
    onSeekRef.current(ratio * maxT);
  };

  const setZoom = (next) => {
    const clamped = Math.min(TRACK_ZOOM_STEPS.length - 1, Math.max(0, next));
    onZoomIndexChange?.(clamped);
  };

  const canZoomIn = zoomIndex < TRACK_ZOOM_STEPS.length - 1;
  const canZoomOut = zoomIndex > 0;
  const playheadLeft = LABEL_COL_PX + 8 + (time / maxT) * timelineWidthPx;

  return (
    <div className="production-track" data-testid="production-track">
      <div className="production-track-header">
        <h4>Production Track{playerName ? ` · ${playerName}` : ''}</h4>
        <div className="production-track-zoom" role="group" aria-label="Production track zoom">
          <button
            type="button"
            className="production-zoom-btn"
            disabled={!canZoomOut}
            onClick={() => setZoom(zoomIndex - 1)}
            aria-label="Zoom out"
          >
            −
          </button>
          <span className="production-zoom-label" data-testid="track-zoom-label">
            {zoom === 1 ? 'Fit' : `${zoom}×`}
          </span>
          <button
            type="button"
            className="production-zoom-btn"
            disabled={!canZoomIn}
            onClick={() => setZoom(zoomIndex + 1)}
            aria-label="Zoom in"
          >
            +
          </button>
        </div>
      </div>
      <div
        className="production-track-scroll"
        ref={scrollRef}
        data-testid="production-track-scroll"
        onScroll={(e) => onScrollLeftChange?.(e.currentTarget.scrollLeft)}
      >
        <div className="production-track-inner" style={{ width: `${innerWidthPx}px` }}>
          <div
            className="production-track-axis"
            style={{ marginLeft: `${LABEL_COL_PX + 8}px` }}
          >
            {axisTicks.map((t) => (
              <span
                key={t}
                className="production-axis-tick"
                style={{ left: `${(t / maxT) * 100}%` }}
              >
                {formatClock(t)}
              </span>
            ))}
          </div>
          <div
            className="production-track-lanes"
            onClick={handleTrackClick}
            role="presentation"
            aria-label="Production track"
          >
            {laidSections.map((section) => (
              <details
                key={section.id}
                className="production-section"
                open
                data-testid={`production-section-${section.id}`}
              >
                <summary
                  className="production-section-summary"
                  style={{ paddingLeft: `${LABEL_COL_PX + 8}px` }}
                >
                  {section.label}
                </summary>
                <div className="production-section-rows">
                  {section.rows.map((row) => (
                    <div
                      key={row.id}
                      className="production-row"
                      style={{ gridTemplateColumns: `${LABEL_COL_PX}px 1fr` }}
                      data-testid={`production-row-${row.id}`}
                    >
                      <div className="production-row-label" title={row.label}>
                        {row.label}
                      </div>
                      <div
                        className={`production-lane lane-${row.rowKind}`}
                        style={{ height: `${LANE_HEIGHT}px`, width: `${timelineWidthPx}px` }}
                      >
                        <ProductionRowBars
                          laid={row.laid}
                          rowKind={row.rowKind}
                          maxT={maxT}
                          timelineWidthPx={timelineWidthPx}
                          onSeekRef={onSeekRef}
                          onHoverRef={onHoverRef}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            ))}
            <div
              className="production-playhead"
              style={{ left: `${playheadLeft}px` }}
              aria-hidden
            />
          </div>
        </div>
      </div>
      <div className="production-track-legend">
        <span className="legend-worker">Worker</span>
        <span className="legend-economy">Economy</span>
        <span className="legend-military">Military</span>
        <span className="legend-army">Army</span>
        <span className="legend-addon">Addon</span>
      </div>
    </div>
  );
};

export default ProductionTrack;
